import { useWizard } from "@/stores/wizard";
import { RequireAuth } from "@/components/RequireAuth";
import { Step0Type } from "@/components/create/Step0Type";
import { Step1Info } from "@/components/create/Step1Info";
import { Step2DropConfig } from "@/components/create/Step2DropConfig";
import { Step2BadgeConfig } from "@/components/create/Step2BadgeConfig";
import { Step3Deploy } from "@/components/create/Step3Deploy";
import { cn } from "@/lib/cn";

const STEP_LABELS = ["Type", "Info", "Config", "Deploy"] as const;

/**
 * Wizard host. Owns the stepper UI + the can-advance gate; each step lives
 * in its own component under components/create/.
 *
 * The 1/1 kind is special-cased: it doesn't need a deploy, so we redirect
 * the user from step 1 to the dedicated /mint page instead of running
 * through Config + Deploy.
 */
export function Create() {
  const step = useWizard((s) => s.step);
  const next = useWizard((s) => s.next);
  const prev = useWizard((s) => s.prev);
  const w = useWizard();
  const isOneOfOne = w.kind === "1of1";

  const canAdvance = () => {
    if (step === 1) return w.name.length > 0 && w.symbol.length > 0;
    if (step === 2) {
      if (w.kind === "badge") return true;
      // Open editions skip the maxSupply check — the contract treats
      // maxSupply=0 as uncapped, but the wizard hides the input behind
      // an explicit toggle so the user never types a magic 0.
      const supplyOk = w.openEdition || w.maxSupply > 0;
      return supplyOk && w.mintPriceEth.length > 0;
    }
    return true;
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="mb-6 text-2xl font-bold">Create</h1>
      <ol className="mb-6 flex gap-2 text-xs">
        {STEP_LABELS.map((label, i) => (
          <li
            key={label}
            className={cn(
              "flex-1 rounded-md border p-2 text-center",
              i === step
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground",
            )}
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      <div className="mb-6">
        {step === 0 && <Step0Type />}
        {step === 1 &&
          (isOneOfOne ? (
            <div className="card text-sm">
              The 1/1 mint doesn't need a deploy. Use the dedicated mint page to upload your art and
              mint a unique token to your wallet.
              <a href="/mint" className="btn-primary mt-3 inline-block">
                Open 1/1 mint
              </a>
            </div>
          ) : (
            <Step1Info />
          ))}
        {step === 2 && (w.kind === "badge" ? <Step2BadgeConfig /> : <Step2DropConfig />)}
        {step === 3 && (
          <RequireAuth>
            <Step3Deploy />
          </RequireAuth>
        )}
      </div>

      <div className="flex justify-between">
        <button className="btn-secondary" onClick={prev} disabled={step === 0}>
          Back
        </button>
        {step < 3 && !isOneOfOne && (
          <button className="btn-primary" onClick={next} disabled={!canAdvance()}>
            Next
          </button>
        )}
      </div>
    </div>
  );
}
